import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import Cookies from 'js-cookie';
import ToolboxLayout from 'layouts/toolboxLayout';
import DemoFormSection from 'components/DemoFormSection';
import WebinarLayout from 'components/WebinarTemplates/Layout';
import WebinarHero from 'components/WebinarTemplates/Hero';
import WebinarOnDemandMainContent from 'components/WebinarTemplates/WebinarOnDemandTemplate/MainContent';
import WebinarOnDemandRelatedPosts from 'components/WebinarTemplates/WebinarOnDemandTemplate/RelatedPosts';
import WebinarSpeakerCarousel from 'components/WebinarTemplates/SpeakerCarousel';
import {
  getUpcomingPosts,
  filterPostsFromSplashAndToolbox,
} from 'components/WebinarPage/utils/postsUtils';
import { getMarketoFormId } from 'utils/marketo';
import { queryStringToCookies } from 'utils/urlHelper';
import useMarketoFormData from 'hooks/useMarketoFormData';
import scroll from 'utils/smoothScroll';
import headsetIcon from 'images/headset-gray.svg';
import useIsClient from 'hooks/useIsClient';
import AddThisSocialSharing from '../components/AddThisSocialSharing';

const WebinarOnDemandTemplate = props => {
  const {
    data: { contentfulWebinar, contentfulWebsiteImage, allContentfulWebinar, onDemandForm },
  } = props;
  const {
    featuredImage,
    callToAction,
    mainContent,
    seo: seoData,
    speakers,
    subtitle,
    keyTakeaways,
    webinarTitle,
    wistiaVideoId,
    wistiaVideo,
    webinarMonthDayYear,
  } = contentfulWebinar;
  const allWebinars = filterPostsFromSplashAndToolbox(
    allContentfulWebinar.edges.map(({ node }) => node)
  );
  const splashPagePosts = filterPostsFromSplashAndToolbox(allWebinars);
  const upcomingPosts = getUpcomingPosts(splashPagePosts);
  const isClient = useIsClient();

  seoData.pageCategory = 'Webinar OnDemand';
  const marketoForm =
    onDemandForm &&
    useMarketoFormData().find(x => x.id === getMarketoFormId(onDemandForm?.marketoForm));
  const getRelatedWebinars = () => {
    const webinarsExcludingCurrent = allWebinars.filter(
      webinar => webinar.id !== contentfulWebinar.id
    );

    const rankedWebinars = webinarsExcludingCurrent.map(webinar => {
      let matches = 0;

      if (webinar.taxonomy?.industries && contentfulWebinar.taxonomy?.industries) {
        if (webinar.taxonomy.industries.includes('All Industries')) {
          matches += contentfulWebinar.taxonomy.industries.length;
        } else {
          matches += webinar.taxonomy.industries.filter(industry =>
            contentfulWebinar.taxonomy.industries.includes(industry)
          ).length;
        }
      }

      if (webinar.taxonomy?.topics && contentfulWebinar.taxonomy?.topics) {
        if (webinar.taxonomy?.topics?.includes('All Topics')) {
          matches += contentfulWebinar.taxonomy.topics.length;
        } else {
          matches += webinar.taxonomy.topics.filter(topic =>
            contentfulWebinar.taxonomy.topics.includes(topic)
          ).length;
        }
      }

      return { matches, ...webinar };
    });

    return rankedWebinars.sort((b, a) => a.matches - b.matches);
  };

  const [formSubmitted, setFormSubmitted] = useState(
    Cookies.get('toolbox_form_submission') === 'true'
  );
  const [selSpeaker, setSelSpeaker] = useState(0);
  const relatedWebinars = getRelatedWebinars();
  const placeholderImage = contentfulWebsiteImage?.image?.fluid;
  const form = props?.data?.contentfulInlineMarketingForm;

  const breadcrumbs = {
    data: [
      {
        title: 'Toolbox',
        icon: null,
        url: '/toolbox',
      },
      {
        title: 'Webinars',
        icon: headsetIcon,
        url: '/webinar',
      },
      {
        title: 'On-Demand Video',
        icon: null,
        url: null,
      },
    ],
    absolute: true,
    theme: 'dark',
  };

  const [search, setSearch] = useState(undefined);

  useEffect(() => {
    queryStringToCookies();
    setSearch(window?.location?.search);
  }, []);

  const onFormSuccess = () => {
    Cookies.set('toolbox_form_submission', 'true');
    setFormSubmitted('true');
  };

  const handleClickSpeaker = index => {
    if (document) {
      const anchor = document.querySelector('#webinar-speakers--carousel');
      scroll('', 130, 500).animateScroll(anchor);
    }

    setSelSpeaker(index);
  };

  if (isClient) {
    return (
      <ToolboxLayout breadcrumbs={breadcrumbs} contentfulSeo={seoData} {...props}>
        <WebinarLayout showAddSocial={formSubmitted}>
          <WebinarHero
            type="on-demand"
            form={form}
            formSubmitted={formSubmitted}
            onFormSuccess={onFormSuccess}
            title={webinarTitle}
            date={webinarMonthDayYear}
          />
          {formSubmitted && <AddThisSocialSharing placement="mobile" />}
          <WebinarOnDemandMainContent
            formSubmitted={formSubmitted}
            marketoForm={marketoForm}
            wistiaVideo={wistiaVideo}
            wistiaVideoId={wistiaVideoId}
            featuredImage={featuredImage}
            title={webinarTitle}
            subtitle={subtitle}
            mainContent={mainContent?.json}
            callToAction={callToAction}
            search={search}
            keyTakeaways={keyTakeaways}
            speakers={speakers}
            upcomingPosts={upcomingPosts}
            onClickSpeaker={handleClickSpeaker}
          />
        </WebinarLayout>

        {speakers && !formSubmitted && (
          <WebinarSpeakerCarousel speakers={speakers} selSpeaker={selSpeaker} />
        )}

        {formSubmitted && (
          <>
            <WebinarOnDemandRelatedPosts
              relatedWebinars={relatedWebinars}
              placeholderImage={placeholderImage}
            />

            <DemoFormSection
              backgroundColor="var(--secondary-color)"
              formTitle="Let's get started"
              description="Explore why 100,000+ contractors trust Service Titan to run their business."
              title="Book a personalized demo today."
              redirectUrl="/"
            />
          </>
        )}
      </ToolboxLayout>
    );
  }
  return null;
};

export const WebinarOnDemandTemplatePageQuery = graphql`
  query WebinarOnDemandBySlug($slug: String!) {
    contentfulWebinar(seo: { pageUrl: { eq: $slug } }) {
      ...Webinar
    }
    allContentfulWebinar {
      edges {
        node {
          ...Webinar
        }
      }
    }
    contentfulWebsiteImage(contentful_id: { eq: "8TSIiE3INirzb6LpyqmvF" }) {
      image {
        fluid(maxWidth: 1600, quality: 100) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
    }
    contentfulInlineMarketingForm(contentful_id: { eq: "6Leir6sWruCSskqB1vgQVB" }) {
      ...InlineMarketingForm
    }
    onDemandForm: contentfulInlineMarketingForm(internalName: { eq: "Webinar On-Demand" }) {
      ...InlineMarketingForm
    }
  }
`;

export default WebinarOnDemandTemplate;
